import { useEffect, useState } from "react";
import { Copy, Download, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn, field } from "@/components/ui-kit";
import { SapUpload } from "@/components/sap/SapUpload";
import { SapPreview } from "@/components/sap/SapPreview";

const URL_CXML = `${process.env.REACT_APP_BACKEND_URL}/api/coupa/cxml`;

const template = async () => {
  const r = await api.get("/coupa/template", { responseType: "blob" });
  Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: "Coupa_PO_Export_Template.csv" }).click();
};

const CxmlSetup = () => {
  const [secret, setSecret] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setSecret(r.data.coupa_shared_secret || "")); }, []);
  const save = async () => {
    try {
      await api.put("/settings", { coupa_shared_secret: secret });
      toast.success("Coupa shared secret saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Coupa cXML PO push (automatic)" testId="coupa-cxml-panel">
      <div className="space-y-3 p-5 text-sm">
        <p className="text-slate-400">Give Amazon's Coupa team this address and shared secret. Coupa posts each new PO as a cXML OrderRequest; the Hub replies 200 OK when the order is created (and the 940 sent), or 400 with the reason.</p>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-sm bg-black/30 px-3 py-2 font-mono text-xs text-emerald-300" data-testid="coupa-cxml-url">{URL_CXML}</code>
          <Button variant="ghost" className={btn.ghost} onClick={() => { navigator.clipboard?.writeText(URL_CXML); toast.success("Copied"); }} data-testid="coupa-copy-url-btn"><Copy className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Shared secret (Coupa supplier credential)" className={field} data-testid="coupa-secret-input" />
          <Button onClick={save} className={btn.primary} data-testid="coupa-secret-save-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
        </div>
      </div>
    </Panel>
  );
};

const CxmlLog = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/coupa/messages").then((r) => setRows(r.data)); }, []);
  return (
    <Panel title="Recent Coupa cXML POs" testId="coupa-cxml-log">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>PO</Th><Th>Result</Th><Th>Message</Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">
            {rows.length === 0 && <tr><Td className="text-slate-500">No cXML POs received yet.</Td></tr>}
            {rows.map((m) => (
              <tr key={m.id}><Td className="font-mono text-slate-500">{ts(m.created_at)}</Td><Td className="font-mono">{m.po_number}</Td>
                <Td className={`font-mono text-xs ${m.code === 200 ? "text-emerald-300" : "text-rose-300"}`}>{m.code}</Td><Td className="text-xs">{m.message}</Td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const CoupaPanel = ({ onImported }) => {
  const [batch, setBatch] = useState(null);
  return (
    <div className="space-y-4">
      <Panel title="Coupa PO export upload" testId="coupa-upload-panel" action={<Button variant="ghost" onClick={template} className={btn.ghost} data-testid="coupa-template-btn"><Download className="mr-1.5 h-4 w-4" /> Coupa template (CSV)</Button>}>
        <div className="space-y-2 p-5">
          <p className="text-xs text-slate-400">Export the POs from the Coupa Supplier Portal (one row per PO line) and drop the file here. The SAP sales order is not needed - key it into SAP as usual.</p>
          <SapUpload path="/coupa/preview" testId="coupa" hint="Drag & drop the Coupa PO export here, or click to choose" onPreview={(b) => { setBatch(b); onImported?.(); }} />
        </div>
      </Panel>
      {batch && <SapPreview key={batch.id} batch={batch} source="Coupa upload" onDone={onImported} />}
      <CxmlSetup />
      <CxmlLog />
    </div>
  );
};
